import React, { useMemo, useContext, useState } from 'react';

import { ADUNITS } from './constants';
import PlatformLevelDFPAdunitConfig from './PlatformLevelDFPAdunitConfig';
//mui imports====
import useCommonStyles from '../../../../../commonStyles';

import { Paper, Button, Tooltip, Box, Chip, Typography, Divider } from '@mui/material';
import Tab from '@mui/material/Tab';
import TabPanel from '@mui/lab/TabPanel';
import SortIcon from '@mui/icons-material/Sort';
//===============
import GeneralTab from '../../../../../Components/GeneralTabAndTabContent';
import PageHeading from '../../../../../Components/PageHeading';
import { ConfigContext } from '../../../../../Contexts/ConfigContext';
import { toast } from 'react-toastify';
import {
	sortAdUnitsByGam,
	allGamsHaveEqualUnits,
	getAllAdUnitArrays
} from '../../../../../utilities/helpers';

const {
	LABEL: { DEFAULT_UNIT, BACKFILL_UNIT, NO_MCM_UNIT },
	KEY: { DEFAULT_UNIT_KEY, BACKFILL_UNIT_KEY, NO_MCM_UNIT_KEY }
} = ADUNITS;

//NOTE : Platform and devices dont have same value!!
const platformAndDevices = [
	{ platform: 'DESKTOP', device: 'desktop' },
	{ platform: 'MOBILE', device: 'mobile' },
	{ platform: 'TABLET', device: 'mobile' }
];

const adUnitTypeKeys = [DEFAULT_UNIT_KEY, BACKFILL_UNIT_KEY, NO_MCM_UNIT_KEY];
const platforms = platformAndDevices.map((p) => p.platform);

const AdUnitsConfigurations = ({ adUnitType, keyData, onPlatformChange }) => {
	const tabListAndPanels = useMemo(() => {
		return platformAndDevices.map((platformAndDevice) => {
			const { platform, device } = platformAndDevice;
			return {
				tab: (
					<Tab
						label={platform}
						value={platform}
						key={platform}
						onClick={() => onPlatformChange?.(platform)}
					/>
				),
				tabPanel: (
					<TabPanel value={platform}>
						<PlatformLevelDFPAdunitConfig
							adUnitType={adUnitType}
							keyData={`${keyData}.${platform}`}
							device={device}
							platform={platform}
						/>
					</TabPanel>
				)
			};
		});
	}, [adUnitType, keyData, onPlatformChange]);

	return (
		<GeneralTab
			tabListAndPanels={tabListAndPanels}
			tabListStyles={{ margin: '1rem 0 4rem 5rem' }}
		/>
	);
};

const DfpAdUnitsConfig = ({ keyData, featureDescription }) => {
	const commonStyleClasses = useCommonStyles();
	const { rawConfig, updateRawConfig } = useContext(ConfigContext);

	const [activeUnitType, setActiveUnitType] = useState(DEFAULT_UNIT_KEY);
	const [activePlatform, setActivePlatform] = useState('DESKTOP');

	const canSortUnits = useMemo(() => {
		return getAllAdUnitArrays(rawConfig, keyData, platforms, adUnitTypeKeys).every(({ arr }) =>
			allGamsHaveEqualUnits(arr)
		);
	}, [rawConfig, keyData]);

	const gamCounts = useMemo(() => {
		const arr = rawConfig[`${keyData}.${activePlatform}.${activeUnitType}`] || [];
		const counts = {};
		for (const unit of arr) {
			counts[unit.gamCode] = (counts[unit.gamCode] || 0) + 1;
		}
		return counts;
	}, [rawConfig, keyData, activePlatform, activeUnitType]);

	const handleSortUnits = () => {
		const updates = {};
		getAllAdUnitArrays(rawConfig, keyData, platforms, adUnitTypeKeys).forEach(({ key, arr }) => {
			updates[key] = sortAdUnitsByGam(arr);
		});
		updateRawConfig({ ...structuredClone(rawConfig), ...updates });
		toast.success('Ad Units sorted by GAM successfully!', {
			position: toast.POSITION.BOTTOM_RIGHT
		});
	};

	const tabListAndPanels = useMemo(() => {
		return [
			{
				tab: (
					<Tab
						label={DEFAULT_UNIT}
						value={DEFAULT_UNIT}
						key={DEFAULT_UNIT}
						onClick={() => setActiveUnitType(DEFAULT_UNIT_KEY)}
					/>
				),
				tabPanel: (
					<TabPanel value={DEFAULT_UNIT}>
						<AdUnitsConfigurations
							adUnitType={DEFAULT_UNIT_KEY}
							keyData={keyData}
							onPlatformChange={setActivePlatform}
						/>
					</TabPanel>
				)
			},
			{
				tab: (
					<Tab
						label={BACKFILL_UNIT}
						value={BACKFILL_UNIT}
						key={BACKFILL_UNIT}
						onClick={() => setActiveUnitType(BACKFILL_UNIT_KEY)}
					/>
				),
				tabPanel: (
					<TabPanel value={BACKFILL_UNIT}>
						<AdUnitsConfigurations
							adUnitType={BACKFILL_UNIT_KEY}
							keyData={keyData}
							onPlatformChange={setActivePlatform}
						/>
					</TabPanel>
				)
			},
			{
				tab: (
					<Tab
						label={NO_MCM_UNIT}
						value={NO_MCM_UNIT}
						key={NO_MCM_UNIT}
						onClick={() => setActiveUnitType(NO_MCM_UNIT_KEY)}
					/>
				),
				tabPanel: (
					<TabPanel value={NO_MCM_UNIT}>
						<AdUnitsConfigurations
							adUnitType={NO_MCM_UNIT_KEY}
							keyData={keyData}
							onPlatformChange={setActivePlatform}
						/>
					</TabPanel>
				)
			}
		];
	}, [keyData]);

	return (
		<Paper className={commonStyleClasses.mainPagePaperMarginStyle} sx={{ p: 3 }}>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'between',
					alignItems: 'flex-start',
					flexWrap: 'wrap',
					gap: 3,
					mb: 3,
					position: 'relative'
				}}
			>
				<Box sx={{ flexGrow: 1, minWidth: '300px' }}>
					<PageHeading
						headingName="Dfp Ad Units"
						headingPath={['Dashboard', 'Framerate', 'NetworkData', 'Dfp Ad Units']}
						description="Manage ad units inventory for desktop, mobile and tablet."
						showInfoButton={true}
						infoMessage={featureDescription}
					/>
				</Box>

				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: { xs: 'flex-start', sm: 'flex-end' },
						gap: 2,
						position: 'absolute',
						top: '5rem',
						right: '8rem'
					}}
				>
					{Object.keys(gamCounts).length > 0 && (
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								alignItems: { xs: 'flex-start', sm: 'flex-end' },
								gap: 1
							}}
						>
							<Typography
								variant="caption"
								sx={{
									color: 'text.secondary',
									fontWeight: 700,
									fontSize: '1.5rem',
									letterSpacing: '0.05em',
									textTransform: 'uppercase'
								}}
							>
								Ad Units Per GAM ({activePlatform} ·{' '}
								{activeUnitType.replace(/([A-Z])/g, ' $1').trim()})
							</Typography>

							<Box
								sx={{
									display: 'flex',
									flexWrap: 'wrap',
									gap: 1,
									justifyContent: { xs: 'flex-start', sm: 'flex-end' }
								}}
							>
								{Object.entries(gamCounts).map(([gam, count]) => (
									<Chip
										key={gam}
										variant="outlined"
										label={
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
												<Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
													{gam}
												</Typography>
												<Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
												<Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
													{count}
												</Typography>
											</Box>
										}
										sx={{
											height: 32,
											borderRadius: '8px',
											backgroundColor: 'background.paper',
											borderColor: 'divider',
											px: 0.5,
											'& .MuiChip-label': { px: 1 }
										}}
									/>
								))}
							</Box>
						</Box>
					)}

					<Tooltip
						title={!canSortUnits ? 'All unique GAMs should have equal number of ad units' : ''}
						arrow
					>
						<span>
							<Button
								variant="contained"
								disableElevation
								size="medium"
								startIcon={<SortIcon />}
								onClick={handleSortUnits}
								disabled={!canSortUnits}
							>
								Sort by GAM
							</Button>
						</span>
					</Tooltip>
				</Box>
			</Box>

			<GeneralTab
				tabListAndPanels={tabListAndPanels}
				tabListStyles={{ margin: '2rem 0 2rem 5rem' }}
			/>
		</Paper>
	);
};

export default DfpAdUnitsConfig;
